import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { reduce, pick } from 'lodash';

import { LocaleService, WindowProxy } from '@sm360/widget-commons-core-services';
import { ValidationService } from '@sm360/widget-commons-forms';
import {
    Account,
    ShiftDigitalService,
    AnalyticsService,
    EVENTS,
    VIRTUAL_PAGEVIEWS as PAGES,
    TRACKING_TAGS,
} from '@sm360/widget-commons-general';
import { ShowroomService, Quote } from '@sm360/widget-commons-vehicle';

import ApplicantInfosForm from 'financing/components/ApplicantInfosForm/ApplicantInfosForm.jsx';
import ResidenceInfosForm from 'financing/components/ResidenceInfosForm/ResidenceInfosForm.jsx';
import EmploymentInfosForm from 'financing/components/EmploymentInfosForm/EmploymentInfosForm.jsx';
import JointInfosForm from 'financing/components/JointInfosForm/JointInfosForm.jsx';

import FinancingService from 'financing/services/FinancingService/FinancingService.js';
import FinancingMapperService from 'financing/services/FinancingMapperService/FinancingMapperService.js';
import { FORM_INDEX } from 'financing/constants/financing.js';

import 'financing/components/Financing/Financing.scss';

class Financing extends React.Component {
    constructor(props) {
        super(props);
        let { account } = props;
        if (typeof props.account !== 'object') {
            account = props.account.toObject();
        }

        this.state = {
            creditApplicationId: '',
            openFormIndex: 0,
            stepCount: 4, // eslint-disable-line react/no-unused-state
            hasCoApplicant: false,
            primaryApplicant: {
                personalInformations: {
                    ...pick(account, ['firstname', 'lastname', 'dateOfBirth']),
                    phoneNumber: account.homePhone,
                    email: account.email1,
                    gender: account.sexId,
                },
                residenceInformations: {
                    address: pick(account, ['address', 'address2', 'city', 'province', 'country', 'postalCode']),
                },
                employmentInformations: {},
                validations: {
                    personalInformations: false,
                    residenceInformations: false,
                    employmentInformations: false,
                    joinApplicationDone: false,
                },
            },
            coApplicant: {
                personalInformations: {
                    firstname: '',
                    lastname: '',
                    phoneNumber: '',
                    email: '',
                    gender: '',
                    dateOfBirth: null,
                },
                residenceInformations: {
                    address: {
                        address: '',
                        address2: '',
                        city: '',
                        province: '',
                        country: '',
                        postalCode: '',
                    },
                },
                employmentInformations: {},
                validations: {
                    personalInformations: false,
                    residenceInformations: false,
                    employmentInformations: false,
                },
            },
        };

        this.nextForm = this.nextForm.bind(this);
        this.previousForm = this.previousForm.bind(this);
        this.onDone = this.onDone.bind(this);
        this.onGoBack = this.onGoBack.bind(this);
        this.saveFinancing = this.saveFinancing.bind(this);
        this.onJointInfoOptionChange = this.onJointInfoOptionChange.bind(this);
        this.updateApplicantInformations = this.updateApplicantInformations.bind(this);
        this.updateResidenceInformations = this.updateResidenceInformations.bind(this);
        this.updateEmploymentInformations = this.updateEmploymentInformations.bind(this);
        this.executeGAVirtualPageView = this.executeGAVirtualPageView.bind(this);
    }

    componentDidMount() {
        AnalyticsService.virtualPageView(PAGES.CHECKOUT_FINANCING_PERSONAL_INFO());
        ShiftDigitalService.updateDrPreQShown(
            this.props?.quote?.id,
            this.props?.quote?.vehicle,
            this.props?.quote?.vehicle?.purchaseMethods?.getSelectedPurchaseMethod()
        );
    }

    componentWillReceiveProps(newProps) {
        const newAccountRecord = newProps.account;
        const oldAccountRecord = this.props.account;

        if (newAccountRecord && oldAccountRecord && !newAccountRecord?.equals(oldAccountRecord)) {
            this.setState({
                primaryApplicant: {
                    personalInformations: {
                        ...pick(newAccountRecord, ['firstname', 'lastname', 'gender', 'dateOfBirth']),
                        phoneNumber: newAccountRecord.homePhone,
                        email: newAccountRecord.email1,
                        gender: newAccountRecord.sexId,
                    },
                    residenceInformations: {
                        address: pick(newAccountRecord, [
                            'address',
                            'address2',
                            'city',
                            'province',
                            'country',
                            'postalCode',
                        ]),
                    },
                    employmentInformations: {},
                    validations: {
                        personalInformations: false,
                        residenceInformations: false,
                        employmentInformations: false,
                        joinApplicationDone: false,
                    },
                },
            });
        }
    }

    onDone(groupResponse, isCoApplicant) {
        function saveCreditApplication() {
            if (groupResponse.valid) {
                this.saveCreditApplication();
                this.nextForm();
            }
        }

        if (groupResponse.name === 'personalInformations') {
            this.updateApplicantInformations(groupResponse, isCoApplicant, saveCreditApplication);
        } else if (groupResponse.name === 'residenceInformations') {
            this.updateResidenceInformations(groupResponse, isCoApplicant, saveCreditApplication);
        } else if (groupResponse.name === 'employmentInformations') {
            this.updateEmploymentInformations(groupResponse, isCoApplicant, saveCreditApplication);
        }
    }

    onGoBack(groupResponse, isCoApplicant) {
        if (groupResponse.name === 'personalInformations') {
            this.updateApplicantInformations(groupResponse, isCoApplicant, this.previousForm);
        } else if (groupResponse.name === 'residenceInformations') {
            this.updateResidenceInformations(groupResponse, isCoApplicant, this.previousForm);
        } else if (groupResponse.name === 'employmentInformations') {
            this.updateEmploymentInformations(groupResponse, isCoApplicant, this.previousForm);
        }
    }

    onJointInfoOptionChange(hasCoApplicant) {
        const { primaryApplicant } = this.state;

        primaryApplicant.validations.joinApplicationDone = true;

        this.setState({
            hasCoApplicant,
            stepCount: hasCoApplicant ? 7 : 4, // eslint-disable-line react/no-unused-state
            primaryApplicant,
        });

        this.saveCreditApplication();
        this.nextForm();
    }

    updateResidenceInformations(groupResponse, isCoApplicant, stateCallback) {
        this.setState((oldState) => {
            const newApplicantResidenceInfo = {
                ...groupResponse.value.inputs,
                housingType: groupResponse.value.housingType,
                housingValues: groupResponse.value.housingValues,
            };

            if (isCoApplicant) {
                const { coApplicant } = oldState;
                coApplicant.residenceInformations = newApplicantResidenceInfo;
                coApplicant.validations.residenceInformations = groupResponse.valid;
                return { coApplicant };
            }

            const { primaryApplicant } = oldState;
            primaryApplicant.residenceInformations = newApplicantResidenceInfo;
            primaryApplicant.validations.residenceInformations = groupResponse.valid;
            return { primaryApplicant };
        }, stateCallback);
    }

    updateEmploymentInformations(groupResponse, isCoApplicant, stateCallback) {
        this.setState((oldState) => {
            if (isCoApplicant) {
                const { coApplicant } = oldState;
                coApplicant.employmentInformations = groupResponse.value;
                coApplicant.validations.employmentInformations = groupResponse.valid;
                return { coApplicant };
            }

            const { primaryApplicant } = oldState;
            primaryApplicant.employmentInformations = groupResponse.value;
            primaryApplicant.validations.employmentInformations = groupResponse.valid;
            return { primaryApplicant };
        }, stateCallback);
    }

    updateApplicantInformations(groupResponse, isCoApplicant, stateCallback) {
        this.setState((oldState) => {
            if (isCoApplicant) {
                const { coApplicant } = oldState;
                coApplicant.personalInformations = groupResponse.value;
                coApplicant.validations.personalInformations = groupResponse.valid;
                return { coApplicant };
            }

            const { primaryApplicant } = oldState;
            primaryApplicant.personalInformations = groupResponse.value;
            primaryApplicant.validations.personalInformations = groupResponse.valid;
            return { primaryApplicant };
        }, stateCallback);
    }

    executeGAVirtualPageView(openFormIndex) {
        switch (openFormIndex) {
            case FORM_INDEX.PERSONAL_INFO:
                AnalyticsService.virtualPageView(PAGES.CHECKOUT_FINANCING_PERSONAL_INFO());
                break;
            case FORM_INDEX.RESIDENCE_INFO:
                AnalyticsService.virtualPageView(PAGES.CHECKOUT_FINANCING_RESIDENCE_INFO());
                break;
            case FORM_INDEX.EMPLOYMENT_INFO:
                AnalyticsService.virtualPageView(PAGES.CHECKOUT_FINANCING_EMPLOYMENT_INFO());
                break;
            case FORM_INDEX.JOINT_INFO:
                AnalyticsService.virtualPageView(PAGES.CHECKOUT_FINANCING_JOINT_INFO());
                break;
            default:
                break;
        }
    }

    nextForm() {
        this.setState(
            (prevState) => {
                return { openFormIndex: prevState.openFormIndex + 1 };
            },
            () => {
                this.executeGAVirtualPageView(this.state.openFormIndex);
                this.props.onFormChange(this.state);
            }
        );
    }

    previousForm() {
        this.setState(
            (prevState) => {
                const { primaryApplicant } = prevState;
                const openFormIndex = prevState.openFormIndex - 1;
                const isInCoApplicantSection = openFormIndex >= 4;

                primaryApplicant.validations.joinApplicationDone = isInCoApplicantSection;

                return {
                    openFormIndex,
                    hasCoApplicant: isInCoApplicantSection,
                    stepCount: isInCoApplicantSection ? 7 : 4,
                    primaryApplicant,
                };
            },
            () => {
                this.executeGAVirtualPageView(this.state.openFormIndex);
                this.props.onFormChange(this.state);
            }
        );
    }

    saveCreditApplication() {
        const creditApplication = {
            id: this.state.creditApplicationId,
            application: this.state,
        };

        return Promise.all([
            ShowroomService.saveCreditApplication(creditApplication, this.props.config),
            this.saveFinancing(),
        ]).then(([showroomResult]) => {
            this.dispatchFinancingCreatedEvent();
            if (showroomResult.id !== this.state.creditApplicationId) {
                this.setState({ creditApplicationId: showroomResult.id });
            }
        });
    }

    saveFinancing() {
        const applicants = this.state.hasCoApplicant
            ? [this.state.primaryApplicant, this.state.coApplicant]
            : [this.state.primaryApplicant];

        const validity = reduce(
            applicants,
            (result, applicant) => {
                return ValidationService.validateForm(applicant.validations);
            },
            false
        );

        if (validity) {
            const financing = FinancingMapperService.getFinancingApplicationFromState(this.state);
            AnalyticsService.eventDataLayer('form|financing', 'submit', 'financing-showroom');
            TRACKING_TAGS.FINANCE_SUBMIT(
                this.props.quote?.vehicle?.getMake(),
                this.props.quote?.vehicle?.getModel(),
                this.props.quote?.vehicle?.year,
                this.props.quote?.vehicle?.getIsVehicleNew() ? 'new' : 'used',
                this.props.quote?.vehicle?.purchaseMethods?.getSelectedPurchaseMethod(),
                this.props.quote?.vehicleId,
                financing?.financing?.PrimaryApplicant?.Email,
                financing?.financing?.PrimaryApplicant?.Phone
            ).then((tags) => AnalyticsService.ga4Trigger(tags));
            return FinancingService.saveFinancing(financing, this.props.leadId, this.props.websiteId).then(() => {
                ShiftDigitalService.updateDrPreQFinish(
                    this.props?.quote?.id,
                    this.props?.quote?.vehicle,
                    this.props?.quote?.vehicle?.purchaseMethods?.getSelectedPurchaseMethod()
                );
                ShiftDigitalService.createFormEvent(
                    this.props.leadId || null,
                    'financing',
                    true,
                    null,
                    false,
                    this.props.quote?.vehicle
                );
            });
        }
        return Promise.resolve();
    }

    dispatchFinancingCreatedEvent() {
        const $window = WindowProxy.get();
        if ($window) {
            $window.document.dispatchEvent(new Event(EVENTS.FINANCING_CREATED));
        }
    }

    render() {
        const { openFormIndex, hasCoApplicant, primaryApplicant, coApplicant, stepCount } = this.state;
        let coApplicantForms;
        const forms = [
            <ApplicantInfosForm
                key={0}
                isOpen={openFormIndex === 0}
                onDone={(newInformations) => this.onDone(newInformations, false)}
                personalInformations={primaryApplicant.personalInformations}
                hasAdobeTagging={this.props.hasAdobeTagging}
                quote={this.props.quote}
            />,
            <ResidenceInfosForm
                key={1}
                isOpen={openFormIndex === 1}
                onDone={(newInformations) => this.onDone(newInformations, false)}
                onBack={(newInformations) => this.onGoBack(newInformations, false)}
                residenceInformations={primaryApplicant.residenceInformations}
                country={this.props.country}
            />,
            <EmploymentInfosForm
                key={2}
                isOpen={openFormIndex === 2}
                onDone={(newInformations) => this.onDone(newInformations, false)}
                onBack={(newInformations) => this.onGoBack(newInformations, false)}
            />,
            <JointInfosForm
                key={3}
                isOpen={openFormIndex === 3}
                onDone={this.onJointInfoOptionChange}
                onBack={this.previousForm}
                hasCoApplicant={hasCoApplicant}
            />,
        ];

        if (hasCoApplicant) {
            coApplicantForms = [
                <ApplicantInfosForm
                    key={4}
                    isOpen={openFormIndex === 4}
                    onDone={(newInformations) => this.onDone(newInformations, true)}
                    onBack={(newInformations) => this.onGoBack(newInformations, true)}
                    personalInformations={coApplicant.personalInformations}
                    isCoApplicant
                    hasAdobeTagging={this.props.hasAdobeTagging}
                    quote={this.props.quote}
                />,
                <ResidenceInfosForm
                    key={5}
                    isOpen={openFormIndex === 5}
                    onDone={(newInformations) => this.onDone(newInformations, true)}
                    onBack={(newInformations) => this.onGoBack(newInformations, true)}
                    residenceInformations={coApplicant.residenceInformations}
                    isCoApplicant
                    country={this.props.country}
                />,
                <EmploymentInfosForm
                    key={6}
                    isOpen={openFormIndex === 6}
                    onDone={(newInformations) => this.onDone(newInformations, true)}
                    onBack={(newInformations) => this.onGoBack(newInformations, true)}
                    isCoApplicant
                />,
            ];
        }

        const isAllFormsDone = openFormIndex >= stepCount;
        return (
            <div className="financing__wrapper">
                <div className="financing__content sr-financing">
                    {forms}
                    {coApplicantForms}
                </div>
                <div className="financing__control">
                    {isAllFormsDone && (
                        <button onClick={() => this.previousForm()} type="button" className="sr-link">
                            {LocaleService.t('FINANCING.BACK')}
                        </button>
                    )}
                    {this.props.children}
                </div>
            </div>
        );
    }
}

Financing.defaultProps = {
    account: new Account(),
    websiteId: null,
    config: {},
    hasAdobeTagging: false,
};

Financing.propTypes = {
    account: PropTypes.instanceOf(Account),
    leadId: PropTypes.number.isRequired,
    onFormChange: PropTypes.func.isRequired,
    children: PropTypes.node.isRequired,
    country: PropTypes.string.isRequired,
    quote: PropTypes.instanceOf(Quote.QuoteModel),
    websiteId: PropTypes.number,
    config: PropTypes.object,
    hasAdobeTagging: PropTypes.bool,
};

Financing.displayName = 'Financing';

function mapStateToProps(state) {
    return {
        account: state.account,
        quote: state.quote,
    };
}

export { Financing };
export default connect(mapStateToProps)(Financing);
